// react
import { useEffect, useCallback } from 'react';
import { UseFormSetValue } from 'react-hook-form';
// @mui
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
// localization
import { useLocales } from 'src/locales';
// types
import { TaxRateDto } from 'src/api';
// hooks
import { useDispatch, useSelector } from 'src/redux/store';
import { getTaxRates } from 'src/redux/slices/tax';
import { useBoolean } from 'src/hooks/use-boolean';
// components
import { RHFSelect } from 'src/components/hook-form';
import TaxSelectOptions from './tax-select-option';
import TaxAddDialog from './tax-add-dialog';

type props = {
  name: string;
  taxRate?: TaxRateDto;
  size?: any;
  typography?: boolean;
  defaultLabel?: boolean;
  width?: number | string;
  setValue: UseFormSetValue<any>;
  onChange?: (value: any) => void;
};

export default function TaxSelect({
  name,
  taxRate,
  size,
  typography = false,
  defaultLabel = true,
  width = '100%',
  setValue,
  onChange,
}: props) {
  const taxRates = useSelector((state) => state.tax.taxRates);

  const { t } = useLocales();

  const openState = useBoolean(false);

  const dispatch = useDispatch();

  useEffect(() => {
    if (taxRates.length === 0 && openState.value !== true) {
      openState.onTrue();
    } else if (taxRates.length > 0 && openState.value !== false) {
      openState.onFalse();
    }
  }, [taxRates, openState]);

  useEffect(() => {
    dispatch(getTaxRates());
  }, [dispatch]);

  const label = t(size === 'small' ? 'VAT' : 'Please select a VAT bracket');

  const handleTaxSelect = useCallback(
    (id: number) => {
      setValue(
        name,
        taxRates?.find((tx) => tx.id === id)
      );
    },
    [setValue, name, taxRates]
  );

  const renderOptions = TaxSelectOptions({ taxRates });

  const renderSelect = (
    <RHFSelect
      size={size}
      name={name}
      label={defaultLabel ? label : ''}
      onChange={(opt) => {
        handleTaxSelect(parseInt(opt.target.value, 10));
        if (onChange) {
          onChange(opt.target.value);
        }
      }}
      value={taxRate?.id ?? -1}
      sx={{ width: { md: width } }}
    >
      <MenuItem key="-1" value="-1" disabled>
        {t('Please select a VAT bracket')}
      </MenuItem>
      {renderOptions.map((option) => (
        <MenuItem key={option.key} value={option.value}>
          {option.children}
        </MenuItem>
      ))}
    </RHFSelect>
  );

  return (
    <Stack spacing={1.5}>
      {typography && <Typography variant="subtitle2">{t('VAT (%)')}</Typography>}

      {renderSelect}

      <TaxAddDialog openState={openState} />
    </Stack>
  );
}
